/**
 * shared — общие элементы KYC-экранов (Kyc / KycSumsub / KycIdenfy):
 * определение исхода по статусу и повторяющиеся карточки состояний.
 */
import type { KycStatus } from "@/lib/api";
import { Card } from "@/components/Screen";
import { Button } from "@/components/Button";
import { Note } from "@/components/Note";
import { Spinner } from "@/components/Spinner";
import { closeApp } from "@/telegram/tg";

// Терминальный исход по статусу. "awaiting" — провайдер (iDenfy/Sumsub) подтвердил,
// но финальный GREEN ещё за брокером/оператором; null — проверка ещё идёт.
export type Outcome = "approved" | "rejected" | "awaiting";

export function resolveOutcome(s: KycStatus): Outcome | null {
  if (s.answer === "GREEN") return "approved";
  if (s.answer === "RED") return "rejected";
  if (s.provider_answer === "RED") return "rejected";
  if (s.provider_answer === "GREEN") return "awaiting";
  return null;
}

export function SkeletonBlock() {
  return (
    <div className="space-y-3">
      <div className="h-24 animate-pulse rounded-2xl bg-surface-card" />
      <div className="h-48 animate-pulse rounded-2xl bg-surface-card" />
    </div>
  );
}

export function ErrorCard({ message }: { message: string }) {
  return <Note variant="danger">{message}</Note>;
}

export function ApprovedCard() {
  return (
    <Card className="text-center">
      <div className="text-3xl">✅</div>
      <div className="mt-2 text-[15px]">Документы приняты. Можно продолжать покупку.</div>
    </Card>
  );
}

export function SubmittedCard({ text }: { text: string }) {
  return (
    <Card className="text-center">
      <Spinner className="mx-auto mb-3 h-6 w-6" />
      <div className="text-[14px] text-white/45">{text}</div>
    </Card>
  );
}

export function RejectedCard({ latest, onRetry }: { latest: KycStatus; onRetry: () => void }) {
  return (
    <Card>
      <div className="text-[15px] font-medium text-red-300">Верификация не пройдена</div>
      {latest.moderation_comment && <div className="mt-2 text-[13px]">{latest.moderation_comment}</div>}
      {latest.reject_labels && latest.reject_labels.length > 0 && (
        <div className="mt-2 font-mono text-[12px] text-white/45">{latest.reject_labels.join(", ")}</div>
      )}
      <Button className="mt-4" onClick={onRetry}>
        Попробовать ещё раз
      </Button>
    </Card>
  );
}

const PHOTO_ACCEPT = "image/jpeg,image/png,image/heic,image/heif,image/webp";

// Пара кнопок «снять/выбрать фото»: камера (getUserMedia — webview Telegram игнорирует
// <input capture>) + галерея. Одинаковая высота и выравнивание, эмодзи инлайн.
export function PhotoSourceButtons({
  busy,
  onCamera,
  onFile,
  accept = PHOTO_ACCEPT,
  className = "",
}: {
  busy?: boolean;
  onCamera: () => void;
  onFile: (f: File) => void;
  accept?: string;
  className?: string;
}) {
  return (
    <div className={`grid grid-cols-2 gap-2 ${busy ? "pointer-events-none opacity-50" : ""} ${className}`}>
      <button
        onClick={onCamera}
        disabled={busy}
        className="flex items-center justify-center gap-2 rounded-xl bg-brand px-4 py-3 text-[14px] font-medium text-surface-base disabled:opacity-50"
      >
        <span className="text-[16px]">📷</span> Камера
      </button>
      <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl bg-surface-raised px-4 py-3 text-[14px]">
        <span className="text-[16px]">🖼</span> Галерея
        <input
          type="file"
          accept={accept}
          disabled={busy}
          className="sr-only"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onFile(f);
            e.target.value = "";
          }}
        />
      </label>
    </div>
  );
}

// Провайдер подтвердил, но финальное решение ещё за брокером/оператором — может быть не
// моментально, поэтому даём явно закрыть окно.
export function AwaitingFinalCard() {
  return (
    <>
      <div className="rounded-2xl border border-amber-400/20 bg-amber-400/10 p-5 text-center">
        <div className="text-3xl">⏳</div>
        <div className="mt-2 text-[15px] font-medium">Документы приняты</div>
        <div className="mt-2 text-[13px] text-white/60">
          Первичная проверка пройдена. Идёт окончательное подтверждение — оно может занять
          некоторое время. Окно можно закрыть: как только проверка завершится, доступ к покупке
          откроется автоматически.
        </div>
      </div>
      <Button variant="ghost" className="mt-4" onClick={() => closeApp()}>
        Закрыть
      </Button>
    </>
  );
}
